/**
 * DocReader — Vollbild-Dokumentenleser
 *
 * Ersetzt den Potree-Viewer und das ContentPanel wenn ein Dokument
 * in der Docs-Section ausgewählt ist.
 * Features: Zurück-Button, Fullscreen-Toggle, Download-Button,
 *           draggable Summary/Notes-Box.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { getDocumentDownloadUrl, getDocumentPreviewUrl } from '../../lib/paperless/api'

interface DocReaderProps {
  docId: string
  summary?: string | null
  onClose: () => void
}


export function DocReader({ docId, summary, onClose }: DocReaderProps) {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // ── Notes (localStorage per docId) ───────────────────────────────────────
  const [note, setNote] = useState(() => {
    try { return localStorage.getItem(`paperless_note_${docId}`) || '' } catch { return '' }
  })
  const [noteSaved, setNoteSaved] = useState(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleNoteChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    setNote(val)
    try {
      if (val) localStorage.setItem(`paperless_note_${docId}`, val)
      else localStorage.removeItem(`paperless_note_${docId}`)
    } catch {}
    // Notify sibling DocumentRow components in the same window
    window.dispatchEvent(new CustomEvent('paperless-note-changed', {
      detail: { docId, note: val },
    }))
    // Show "Gespeichert"-indicator briefly
    setNoteSaved(true)
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => setNoteSaved(false), 1800)
  }, [docId])

  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
  }, [])

  // ── Drag floating ─────────────────────────────────────────────────────────
  const boxRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const box = boxRef.current
    const iframe = iframeRef.current
    if (!box) return

    // Disable iframe event swallowing during drag
    if (iframe) iframe.style.pointerEvents = 'none'

    const boxRect    = box.getBoundingClientRect()
    const parentRect = ((box.offsetParent as HTMLElement) || document.body).getBoundingClientRect()

    // Switch to parent-relative absolute positioning at current position
    box.style.transition = 'none'
    box.style.right  = 'auto'
    box.style.bottom = 'auto'
    box.style.left   = (boxRect.left - parentRect.left) + 'px'
    box.style.top    = (boxRect.top  - parentRect.top)  + 'px'

    const ox = e.clientX - boxRect.left
    const oy = e.clientY - boxRect.top

    const onMove = (ev: MouseEvent) => {
      const pRect = ((box.offsetParent as HTMLElement) || document.body).getBoundingClientRect()
      box.style.left = (ev.clientX - ox - pRect.left) + 'px'
      box.style.top  = (ev.clientY - oy - pRect.top)  + 'px'
    }

    const stopDrag = () => {
      if (iframe) iframe.style.pointerEvents = ''
      box.style.transition = ''
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup',    stopDrag)
      document.removeEventListener('mouseleave', stopDrag)
    }

    document.addEventListener('mousemove',  onMove)
    document.addEventListener('mouseup',    stopDrag)
    document.addEventListener('mouseleave', stopDrag)
  }, [])

  // ── Fullscreen ────────────────────────────────────────────────────────────
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {})
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {})
    }
  }, [])

  const handleFullscreenChange = useCallback(() => {
    setIsFullscreen(!!document.fullscreenElement)
  }, [])

  useState(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  })

  const handleDownload = useCallback(() => {
    const link = document.createElement('a')
    link.href = getDocumentDownloadUrl(Number(docId))
    link.download = ''
    link.click()
  }, [docId])


  return (
    <div
      ref={containerRef}
      className="doc-reader"
      data-component="DocReader"
    >
      {/* ── Toolbar ──────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '2.5em',
        padding: '0 0.75em',
        background: '#3a3d40',
        borderBottom: '1px solid #2a2d30',
        flexShrink: 0,
      }}>
        {/* Left: Back */}
        <button
          onClick={onClose}
          title="Zurück zur Liste"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375em',
            background: 'none',
            border: 'none',
            color: '#d1d5db',
            cursor: 'pointer',
            fontSize: '0.8125em',
            fontFamily: 'inherit',
            padding: '0.25em 0.5em',
            borderRadius: '0.25em',
            transition: 'background 0.15s ease, color 0.15s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#4a4d50'; e.currentTarget.style.color = '#fff' }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#d1d5db' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Zurück
        </button>

        {/* Right: Fullscreen + Download */}
        <div style={{ display: 'flex', gap: '0.25em', alignItems: 'center' }}>
          <ToolbarButton
            title={isFullscreen ? 'Vollbild beenden' : 'Vollbild'}
            onClick={toggleFullscreen}
          >
            {isFullscreen ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 14 8 14 8 18"/><polyline points="20 10 16 10 16 6"/>
                <line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
                <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
            )}
          </ToolbarButton>

          <ToolbarButton title="Herunterladen" onClick={handleDownload}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </ToolbarButton>
        </div>
      </div>

      {/* ── Document iframe + floating Summary/Notes box ─── */}
      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        <iframe
          ref={iframeRef}
          src={getDocumentPreviewUrl(Number(docId))}
          style={{ width: '100%', height: '100%', border: 'none', background: '#525659' }}
          title="Dokumenten-Vorschau"
        />

        {/* Summary + Notes card (always shown, draggable) */}
        <div
          ref={boxRef}
          className="doc-viewer-summary"
        >
          {/* Drag handle */}
          <div className="doc-viewer-summary__handle" onMouseDown={handleDragStart}>
            {/* Sparkle icon */}
            <svg
              className="dok-card-ai-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
              <path d="M20 3v4"/><path d="M22 5h-4"/>
            </svg>
            <span className="doc-viewer-summary__label">Zusammenfassung</span>
            {/* Drag icon */}
            <svg
              className="doc-viewer-summary__drag-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              width="18"
              height="18"
              aria-hidden="true"
            >
              <path d="M12 3v18M3 12h18M12 3l-3 3m3-3 3 3M12 21l-3-3m3 3 3-3M3 12l3-3m-3 3 3 3M21 12l-3-3m3 3-3 3"/>
            </svg>
          </div>

          {/* Summary text (only when present) */}
          {summary && (
            <div className="doc-viewer-summary__body">
              <div className="doc-viewer-summary__skeleton" aria-hidden="true" />
              <p>{summary}</p>
            </div>
          )}

          {/* Notes input */}
          <div className="doc-viewer-summary__notes-wrap">
            <div className="doc-viewer-summary__notes-header">
              <span className="doc-viewer-summary__notes-label">Notiz</span>
              <span className={`doc-viewer-summary__saved${noteSaved ? ' is-visible' : ''}`}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Gespeichert
              </span>
            </div>
            <textarea
              className="doc-viewer-summary__notes"
              placeholder="Notiz zum Dokument…"
              value={note}
              onChange={handleNoteChange}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// ── ToolbarButton ─────────────────────────────────────────────────────────────

function ToolbarButton({ title, onClick, children }: {
  title: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '2.25em',
        height: '2.25em',
        borderRadius: '0.25em',
        border: 'none',
        background: 'transparent',
        color: '#d1d5db',
        cursor: 'pointer',
        transition: 'background 0.15s ease, color 0.15s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#4a4d50'; e.currentTarget.style.color = '#fff' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#d1d5db' }}
    >
      {children}
    </button>
  )
}
